import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import Button from "../../../shared/components/button/Button";
import { Profile } from "../types/profile.type";
import { getItemOnIndex } from "../../../utils";
import SearchableDropdown from "../../../shared/components/searchable-dropdown/SearchableDropdown";
import { useGetUsers } from "../../../api/profile";
import { User } from "../../../shared/types/user.type";

const DialogHeader = styled(DialogTitle)`
  background: ${(props) => props.theme.greyBackground};
  &.dialog-title {
    padding: 10px 24px;
  }
`;

const DialogBody = styled(DialogContent)`
  display: flex;
  &.dialog-body {
    padding: 24px 24px 12px !important;
  }
`;

const DialogFooter = styled(DialogContent)`
  text-align: right;

  &.dialog-footer {
    padding: 0 20px 12px;
  }

  button {
    margin-left: 8px;
    min-width: 100px;
  }
`;

export interface AssignProfileConfirmationProps extends DialogProps {
  onConfirm(userId: number): void;
  onCancel(): void;
  open: boolean;
  title: string;
  actionProfile?: Profile;
  isBulkAssign?: boolean;
  selectedProfilesLength: number;
}

const AssignProfileConfirmation: React.FC<AssignProfileConfirmationProps> = ({
  title,
  onConfirm,
  onCancel,
  actionProfile,
  isBulkAssign = false,
  selectedProfilesLength,
  ...extraProps
}) => {
  const [users, setUsers] = useState(Array<User>);

  const { t: translate } = useTranslation(["profile", "common"]);

  let selectedUserId: string | number;

  const { data: usersData, isLoading: usersDataLoading } = useGetUsers();

  useEffect(() => {
    if (usersData != undefined && !usersDataLoading) {
      setUsers(usersData);
    }
  }, [usersData]);

  const onUserChange = (userId: string | number) => {
    selectedUserId = userId;
  };

  return (
    <>
      <Dialog
        {...extraProps}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogHeader id="alert-dialog-title" className="dialog-title">
          {title}
          <IconButton
            onClick={onCancel}
            aria-label="close-button"
            sx={{
              position: "absolute",
              right: 12,
              top: 6,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>
        <DialogBody className="dialog-body">
          <DialogContentText id="alert-dialog-description">
            {isBulkAssign ? (
              <>
                {translate("bulkProfileAssignConfirmation").replace(
                  "$noOfProfiles",
                  selectedProfilesLength.toString()
                )}
                :
              </>
            ) : (
              <>
                {getItemOnIndex(
                  translate("profileAssignConfirmation"),
                  "$profile-name",
                  0
                )}
                <strong data-private="true">{actionProfile?.name}</strong>
                {getItemOnIndex(
                  translate("profileAssignConfirmation"),
                  "$profile-name",
                  1
                )}
                :
              </>
            )}
          </DialogContentText>
          {
            <SearchableDropdown
              list={users}
              onItemChange={onUserChange}
              selectedId={actionProfile?.assignedToId || undefined}
              title="User"
            />
          }
        </DialogBody>
        <DialogFooter className="dialog-footer">
          <Button variant="grey" onClick={onCancel} aria-label="cancel">
            {translate("cancel", { ns: "common" })}
          </Button>
          <Button
            variant="success"
            onClick={() => onConfirm(selectedUserId as number)}
            aria-label="confirm"
          >
            {translate("confirm", { ns: "common" })}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AssignProfileConfirmation;
